import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { onMessage } from "firebase/messaging";

import AdminTopBar from "../components/AppBar";
import BookingCard from "../components/booking/BookingCard";
import LoadingScreen from "../../utils/LoadingScreen";
import Filters from "../components/Filters";
import BookingTable from "../components/booking/BookingTable";
import Modal from "../../components/Modal";

import { messaging } from "../../firebase/firebase";
import { useNotification } from "../../contexts/NotificationContext";
import { useBookings } from "../hooks/useBookings";
import { useCatalog } from "../../contexts/CatalogContext";

export default function UpcomingBookingsPage() {
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [lastClipboardText, setLastClipboardText] = useState<string | null>(
    null,
  );
  const [visualization, setVisualization] = useState<"card" | "table">("card");
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { driversMap, vehiclesMap } = useCatalog();

  const { rawBookings, loading, totalCount, loadMore } =
    useBookings("upcoming");

  const readFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text !== lastClipboardText) {
        setLastClipboardText(text);
        const phoneNumberRegex =
          /\(?\+[0-9]{1,3}\)? ?-?[0-9]{1,3} ?-?[0-9]{3,5} ?-?[0-9]{4}( ?-?[0-9]{3})? ?(\w{1,10}\s?\d{1,6})?/;
        if (phoneNumberRegex.test(text)) {
          setPhoneNumber(text);
        }
      }
    } catch (err) {
      console.log(err);
    }
  }, [lastClipboardText]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      readFromClipboard();
    }, 2000);
    return () => clearInterval(intervalId);
  }, [readFromClipboard]);

  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload: any) => {
      showNotification(
        "info",
        payload.notification?.body,
        payload.notification?.title,
      );
    });
    return () => unsubscribe();
  }, [showNotification]);

  const filteredBookings = useMemo(() => {
    if (!searchQuery) return rawBookings;
    return rawBookings.filter((item: any) =>
      ["origin", "destination", "phone", "name"].some((key) => {
        const value = item[key];
        return (
          typeof value === "string" &&
          value.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }),
    );
  }, [rawBookings, searchQuery]);

  const groupedBookings = useMemo(() => {
    return filteredBookings.reduce<Record<string, any[]>>((acc, booking) => {
      const dateKey = dayjs(booking.date).format("YYYY-MM-DD");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(booking);
      acc[dateKey].sort((a, b) => a.date - b.date);
      return acc;
    }, {});
  }, [filteredBookings]);

  const dataLength = useMemo(() => {
    if (visualization === "table") {
      return filteredBookings.length;
    }
    return Object.values(groupedBookings).reduce(
      (total, group) => total + group.length,
      0,
    );
  }, [filteredBookings, groupedBookings, visualization]);

  const handleAddBooking = () => navigate("/admin-management/add");

  const handleOpenWithNumber = (number: string) => {
    const cleanNumber = number.replace(/\D/g, "");
    navigate(`/admin-management/add?number=${cleanNumber}`);
  };

  const formatDate = (date: string) => {
    if (dayjs(date).isSame(dayjs(), "day")) {
      return "HOJE";
    }
    return dayjs(date).format("DD MMMM YYYY");
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-24 text-white">
      <AdminTopBar handleAdd={handleAddBooking} />

      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-6">
        <Filters
          active={visualization}
          searchBarDisabled={visualization === "table"}
          onVisualizationChange={(view) =>
            setVisualization(view as "card" | "table")
          }
          onSearchChange={setSearchQuery}
        />

        {visualization === "card" && (
          <div className="space-y-6">
            {Object.entries(groupedBookings)
              .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
              .map(([date, bookings]) => (
                <section key={date} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-[0.4em] text-white">
                      {formatDate(date)}
                    </p>
                    {bookings.length >= 2 && (
                      <span className="rounded-full bg-white/10 px-3 py-1 text-xs">
                        {bookings.length} reservas
                      </span>
                    )}
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {bookings.map((booking) => (
                      <BookingCard
                        key={booking.id}
                        booking={booking}
                        driver={driversMap[booking.driver]}
                        vehicle={vehiclesMap[booking.vehicle]}
                        done={false}
                      />
                    ))}
                  </div>
                </section>
              ))}
            {filteredBookings.length < totalCount && (
              <button
                type="button"
                onClick={loadMore}
                className="w-full rounded-2xl border border-white/15 py-3 text-sm text-white/80 hover:bg-white/5"
              >
                Carregar mais
              </button>
            )}
          </div>
        )}

        {visualization === "table" && (
          <BookingTable rawData={filteredBookings} />
        )}

        {dataLength < 1 && (
          <p className="text-center text-sm text-white/60">
            Nenhuma reserva para mostrar.
          </p>
        )}
      </main>

      {phoneNumber && (
        <Modal
          open={!!phoneNumber}
          onClose={() => setPhoneNumber(null)}
          onClick={() => handleOpenWithNumber(phoneNumber)}
          title={phoneNumber}
          description="Parece que você copiou um telefone. Deseja criar uma nova reserva?"
          closeButton="Não"
          continueButton="Adicionar reserva"
        />
      )}
    </div>
  );
}
